import { dialog } from "nq-component";
import BaseListPresenter from "../../../base/BaseListPresenter";
import ExportCSVFunction from "../../../components/ExportCSVFunction";
import {
  REPORT_MENUS,
  DEFAULT_REPORT,
  buildSchemaFromColumns,
  getReportDisplayFields,
  MONTHS,
} from "./reportConfig";
import ReportService, {
  EMPLOYEE_SCOPED_SERVICES,
  getSelectedEmployee,
  normalizeHierarchy,
} from "./ReportService";
import {
  ORG_HIERARCHY_FIELDS,
  hasOrganizationFields,
  loadOrganizationNameMaps,
  resolveOrganizationFields,
} from "./organizationExportUtils";

class ReportPagePresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.view = view;
    this.reportService = new ReportService(findObjectUseCase);
    this.options = REPORT_MENUS;
    this.allObjects = [];
  }

  /**
   * nq-component dialog.close() does nodes.pop() without initializing nodes.
   * Calling close before any dialog.fire() throws TypeError.
   */
  closeBusyDialog() {
    try {
      if (Array.isArray(dialog.nodes) && dialog.nodes.length > 0) {
        dialog.close();
      }
    } catch (_) {
      /* ignore — nothing open */
    }
  }

  componentDidMount() {
    this.init();
    // Do not auto-fetch large datasets on page open
    this.view.setObjects([]);
    this.view.setCount?.(0);
    this.hideProgress();
  }

  init() {
    // Keep in sync with ReportPage state.limit (Table pageSize). Mismatch
    // caused empty pages (e.g. UI pageSize 20 vs presenter slice of 50).
    this.limit = this.view.state?.limit || 50;
    this.where = { ...(DEFAULT_REPORT.where || {}) };
    this.search = {};
    this.filter = {};
    this.include = ["employee", "position", "employmentType", "nonRegPosition", "supervisor"];
    this.sort = { createdAt: -1 };
    this.progress = false;
    this.current = 1;
    this.objects = [];
    this.allObjects = [];
  }

  getPageLimit() {
    const limit = Number(this.view.state?.limit) || this.limit || 20;
    this.limit = limit;
    return limit;
  }

  createQuery() {
    const limit = this.getPageLimit();
    const skip = (this.current - 1) * limit;
    const query = {
      limit,
      skip: skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  getSelectedReport() {
    return this.view.state.construction || DEFAULT_REPORT;
  }

  /**
   * Employee records / Organizational Structure hide separated employees in
   * Generate. Export must apply the same filter or those rows reappear.
   */
  async filterOutSeparatedEmployees(objects = []) {
    const separated = await this.findObjectUseCase.execute("seperated", {
      keys: ["employee"],
      limit: 5000,
    });
    const separatedEmployeeIds = new Set(
      (separated || []).map((item) => item?.employee?.id).filter(Boolean),
    );
    return (objects || []).filter((obj) => !separatedEmployeeIds.has(obj.id));
  }

  /**
   * Prefer the generated table set so export cannot include employees the
   * user is not seeing. Fall back to separated filtering when Generate was
   * not run yet.
   */
  async alignExportObjectsWithTable(objects = [], report) {
    if (
      !["Employee records", "Organizational Structure"].includes(report?.name)
    ) {
      return objects;
    }

    if (this.allObjects.length > 0) {
      const tableIds = new Set(
        this.allObjects.map((obj) => obj.id).filter(Boolean),
      );
      return (objects || []).filter((obj) => tableIds.has(obj.id));
    }

    return this.filterOutSeparatedEmployees(objects);
  }

  getReportFilters() {
    return this.view.state.reportFilters || {};
  }

  validateFilters(report, filters) {
    const defs = report.filters || [];
    for (const def of defs) {
      if (!def.required) continue;
      if (def.type === "dateRange") {
        const dr = filters[def.key] || filters.dateRange || {};
        const hasStart =
          dr.start ||
          dr.$gte ||
          dr.createdAt?.$gte ||
          dr.obDates?.$gte ||
          dr.start?.$gte;
        const hasEnd =
          dr.end ||
          dr.$lte ||
          dr.updatedAt?.$lte ||
          dr.createdAt?.$lte ||
          dr.dateApproved?.$lte ||
          dr.obDates?.$lte ||
          dr.end?.$lte;
        if (!hasStart || !hasEnd) {
          throw new Error(`${def.label || "Date Range"} is required.`);
        }
      } else if (def.type === "month" || def.type === "year") {
        if (!filters[def.key]) {
          throw new Error(`${def.label || def.key} is required.`);
        }
      } else if (!filters[def.key]) {
        throw new Error(`${def.label || def.key} is required.`);
      }
    }
  }

  reportRequiresEmployeeScope(report) {
    return (
      report?.mode === "service" &&
      EMPLOYEE_SCOPED_SERVICES.has(report.service)
    );
  }

  async generateReport() {
    const report = this.getSelectedReport();
    const filters = this.getReportFilters();
    try {
      this.validateFilters(report, filters);
      if (this.reportRequiresEmployeeScope(report)) {
        this.reportService.assertEmployeeScope(filters, { required: true });
      }
    } catch (e) {
      const message = e?.message || String(e);
      this.view.setState({ error: null, isLoading: false });
      this.view.showError(message);
      return;
    }

    // Cancel any previous Reports run (does not affect Timesheet BiometricDTR).
    this.reportService.cancelRun();
    try {
      this.findObjectUseCase?.abort?.();
    } catch (_) {
      /* ignore */
    }

    const generateToken = (this._generateToken = (this._generateToken || 0) + 1);

    this.view.setState({ isLoading: true, generated: false, error: null });
    this.current = 1;
    this.view.setCurrent?.(1);
    this.allObjects = [];
    this.objects = [];
    this.getPageLimit();
    this.view.showBusyDialog?.("Generating report...");

    let showedResultDialog = false;
    try {
      if (report.mode === "service") {
        const rows = await this.reportService.run(report.service, filters);
        if (generateToken !== this._generateToken) return;
        this.allObjects = rows || [];
        this.applyClientPagination();
        this.view.setState({
          generated: true,
          count: this.allObjects.length,
          summary: this.buildSummary(report, this.allObjects, filters),
        });
      } else {
        const collectionWhere = { ...(filters.collectionWhere || {}) };
        const employee = getSelectedEmployee(filters);
        if (employee?.id) {
          if (report.collection === "employees") {
            collectionWhere.id = employee.id;
          } else {
            collectionWhere["employee.id"] = employee.id;
          }
        }
        if (report.key === "separated_employees" && filters.type) {
          collectionWhere.type = filters.type;
        }
        this.where = {
          ...(report.where || {}),
          ...collectionWhere,
        };
        // Load full result set — do not use table page limit (was capping at ~50).
        const rows = await this.findAllCollectionObjects(report);
        if (generateToken !== this._generateToken) return;
        this.allObjects = rows || [];
        this.applyClientPagination();
        this.view.setState({
          generated: true,
          count: this.allObjects.length,
          summary: this.buildSummary(report, this.allObjects, filters),
        });
      }
    } catch (error) {
      if (generateToken !== this._generateToken) return;
      console.error(error);
      const message = this.formatReportError(error);
      this.closeBusyDialog();
      this.view.setState({ error: null, isLoading: false });
      this.view.showError(message);
      showedResultDialog = true;
    } finally {
      if (generateToken === this._generateToken) {
        this.view.setState({ isLoading: false });
        // Do not close after showError — that would dismiss the error dialog.
        if (!showedResultDialog) this.closeBusyDialog();
      }
    }
  }

  formatReportError(error) {
    if (error?.isRateLimit || error?.isScopeTooLarge || error?.isAborted) {
      return error.message;
    }
    const raw = error?.message || String(error || "");
    const lower = raw.toLowerCase();
    if (
      lower.includes("429") ||
      lower.includes("too many requests") ||
      lower.includes("rate limit") ||
      lower.includes("unable to connect") ||
      lower.includes("access-control-allow-origin")
    ) {
      return "Too many requests to the server (rate limited). Narrow the Employee or Office filter and try again in a minute.";
    }
    return raw || "Failed to generate report.";
  }

  formatSummaryDate(value) {
    if (!value) return "";
    if (typeof value === "string" || typeof value === "number") {
      return String(value).slice(0, 10);
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString().slice(0, 10);
    }
    if (typeof value === "object") {
      const raw =
        value.$gte ||
        value.$lte ||
        value.start ||
        value.end ||
        value.iso ||
        value.isoString;
      return raw ? this.formatSummaryDate(raw) : "";
    }
    return "";
  }

  buildSummary(report, rows, filters) {
    const parts = [];
    if (filters.month && filters.year) {
      parts.push(`Period: ${filters.month} ${filters.year}`);
    } else if (filters.dateRange || filters.dateRequested || filters.obDates) {
      const dr =
        filters.dateRequested || filters.obDates || filters.dateRange || {};
      const start = this.formatSummaryDate(
        dr.start || dr.$gte || dr.createdAt || dr.obDates || dr.dateFrom,
      );
      const end = this.formatSummaryDate(
        dr.end ||
          dr.$lte ||
          dr.updatedAt ||
          dr.dateApproved ||
          dr.createdAt?.$lte ||
          dr.obDates ||
          dr.dateTo,
      );
      if (start || end) {
        parts.push(`Period: ${start || "…"}${end ? ` – ${end}` : ""}`);
      }
    }
    const employee = getSelectedEmployee(filters);
    if (employee?.id) {
      const name =
        `${employee.Firstname || ""} ${employee.surname || ""}`.trim();
      parts.push(`Employee: ${name || employee.id}`);
    }
    parts.push(`Records: ${(rows || []).length}`);
    return parts.join(" · ");
  }

  applyClientPagination() {
    const limit = this.getPageLimit();
    const start = (this.current - 1) * limit;
    const page = this.allObjects.slice(start, start + limit);
    this.objects = page;
    this.view.setObjects(page);
    this.view.setCurrent(this.current);
    this.view.setCount?.(this.allObjects.length);
  }

  /**
   * Collection reports (e.g. Employee records): load every matching row once.
   * createQuery() uses the table page size — Generate must not stop at one page.
   */
  async findAllCollectionObjects(report) {
    const collection = this.view.getCollectionName();
    const PAGE = 500;
    const where = {
      ...this.where,
      ...this.search,
      ...this.filter,
      "acl.read": { $exists: true },
    };
    const keys = [...(report.displayField || [])];
    const all = [];
    let skip = 0;

    while (true) {
      const batch = await this.findObjectUseCase.execute(collection, {
        where,
        include: this.include,
        ...(keys.length ? { keys } : {}),
        ...(this.sort ? { sort: this.sort } : {}),
        limit: PAGE,
        skip,
      });
      if (!batch?.length) break;
      all.push(...batch);
      if (batch.length < PAGE) break;
      skip += PAGE;
    }

    if (report.name === "List of Plantilla Item") {
      all.sort((a, b) => {
        const titleA = (a.position_title || "").toLowerCase();
        const titleB = (b.position_title || "").toLowerCase();
        return titleA.localeCompare(titleB);
      });
    }

    if (report.displayField?.includes("positionLabel")) {
      all.forEach((obj) => {
        if (obj.employmentType?.types === "Plantilla") {
          obj.positionLabel = obj.position?.position_title || "";
        } else if (obj.employmentType?.types === "Non-Plantilla") {
          obj.positionLabel = obj.nonRegPosition?.positionTitle || "";
        }
      });
    }

    let filterObject = all;
    if (
      ["Employee records", "Organizational Structure"].includes(report.name)
    ) {
      filterObject = await this.filterOutSeparatedEmployees(all);
    }

    return filterObject;
  }

  async findObjects({ reset = false } = {}) {
    const collection = this.view.getCollectionName();
    const report = this.getSelectedReport();
    const query = this.createQuery();
    query.keys = [...(report.displayField || [])];
    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);

      if (report.name === "List of Plantilla Item") {
        objects.sort((a, b) => {
          const titleA = (a.position_title || "").toLowerCase();
          const titleB = (b.position_title || "").toLowerCase();
          return titleA.localeCompare(titleB);
        });
      }

      if (report.displayField?.includes("positionLabel")) {
        objects.forEach((obj) => {
          if (obj.employmentType?.types === "Plantilla") {
            obj.positionLabel = obj.position?.position_title || "";
          } else if (obj.employmentType?.types === "Non-Plantilla") {
            obj.positionLabel = obj.nonRegPosition?.positionTitle || "";
          }
        });
      }

      let filterObject = objects;
      if (
        ["Employee records", "Organizational Structure"].includes(report.name)
      ) {
        filterObject = await this.filterOutSeparatedEmployees(objects);
      }

      if (reset) {
        this.objects = filterObject;
      } else {
        this.objects = this.objects.concat(filterObject);
      }
      this.view.setObjects(filterObject);
      this.view.setCurrent(this.current);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  handleActionBtn(object, showModal) {
    this.view.setState({ object, [showModal]: true });
  }

  setCurrentPage(page) {
    this.current = page;
    this.view.setCurrent(page);
    const report = this.getSelectedReport();
    // After Generate, collection reports keep full result in allObjects (client pages).
    if (
      report.mode === "service" ||
      (report.mode === "collection" && this.allObjects.length > 0)
    ) {
      this.applyClientPagination();
      return;
    }
    this.documents = [];
    this.view.setObjects([]);
    this.findObjects();
  }

  handelTableDataChange(value) {
    this.where = { ...(value.where || {}) };
    this.allObjects = [];
    this.objects = [];
    // Stop any in-flight Reports queries from the previous report selection
    this.reportService?.cancelRun?.();
    this._generateToken = (this._generateToken || 0) + 1;
    try {
      this.findObjectUseCase?.abort?.();
    } catch (_) {
      /* ignore */
    }
    this.view.setState({
      construction: value,
      objects: [],
      count: 0,
      generated: false,
      summary: "",
      error: null,
      reportFilters: {},
      filters: {},
      committee: {},
      designation: {},
      filterEpoch: (this.view.state.filterEpoch || 0) + 1,
    });
    // Lazy-load committee data only when needed (avoid rate-limit on page open)
    if (value?.isCommitteeFilter) {
      this.view.ensureCommitteeFiltersLoaded?.();
    }
  }

  onClear() {
    this.handelTableDataChange(DEFAULT_REPORT);
  }

  resetFilters() {
    this.allObjects = [];
    this.objects = [];
    this.current = 1;
    try {
      this.findObjectUseCase?.abort?.();
    } catch (_) {
      /* ignore */
    }
    this.view.setState({
      objects: [],
      count: 0,
      generated: false,
      summary: "",
      error: null,
      reportFilters: {},
      filters: {},
      committee: {},
      designation: {},
      current: 1,
      filterEpoch: (this.view.state.filterEpoch || 0) + 1,
    });
  }

  setReportFilter(key, value) {
    const reportFilters = { ...this.getReportFilters() };
    let next = value;
    if (key === "employee") {
      next = getSelectedEmployee({ employee: value });
    }
    if (key === "hierarchy") {
      next = normalizeHierarchy(value);
    }
    const isEmpty =
      next === undefined ||
      next === null ||
      next === "" ||
      (Array.isArray(next) && next.length === 0) ||
      (typeof next === "object" &&
        !Array.isArray(next) &&
        Object.keys(next).length === 0);
    if (isEmpty) {
      delete reportFilters[key];
    } else {
      reportFilters[key] = next;
    }
    this.view.setState({ reportFilters });
  }

  manualFilter(value, label) {
    if (label === "committee") {
      let where = {};
      if (value && Object.keys(value).length > 0) {
        const idList = (value.committeeRelation || [])
          .map((relation) => relation.employee?.id)
          .filter(Boolean);
        // Keep previous Generate-on-click flow; store where for Generate.
        // Empty $in would return no rows — treat as no members found.
        where = { id: { $in: idList } };
        if (idList.length === 0) {
          this.view.showError?.(
            "This committee has no members (or member data could not be loaded).",
          );
        }
      }
      this.setReportFilter("collectionWhere", where);
      // Reset designation when committee changes
      this.view.setState({ committee: value || {}, designation: {} });
      return;
    }

    if (
      label === "designation" &&
      value &&
      Object.keys(value).length > 0 &&
      this.view.state.committee?.committeeRelation?.length > 0
    ) {
      const idList = this.view.state.committee.committeeRelation
        .filter((relation) => relation.Designation?.id === value.id)
        .map((relation) => relation.employee?.id)
        .filter(Boolean);
      this.setReportFilter("collectionWhere", { id: { $in: idList } });
      this.view.setState({ designation: value });
      return;
    }

    // Designation cleared → roll back to full committee member list
    if (
      label === "designation" &&
      (!value || Object.keys(value).length === 0) &&
      this.view.state.committee?.committeeRelation?.length > 0
    ) {
      const idList = this.view.state.committee.committeeRelation
        .map((relation) => relation.employee?.id)
        .filter(Boolean);
      this.setReportFilter("collectionWhere", { id: { $in: idList } });
      this.view.setState({ designation: {} });
      return;
    }

    this.view.setState({ [label]: value || {} });
  }

  async handleHierarchyChange(hierarchy) {
    this.setReportFilter("hierarchy", hierarchy);
    const report = this.getSelectedReport();
    if (report.mode === "collection") {
      const currentFilters = { ...(this.view.state.filters || {}) };
      Object.entries(hierarchy || {}).forEach(([key, value]) => {
        if (value) currentFilters[key] = value;
        else delete currentFilters[key];
      });
      this.view.setState({ filters: currentFilters }, () => {
        // Keep for legacy filterSubmit when Generate runs
        this.setReportFilter("collectionWhere", {
          ...(this.getReportFilters().collectionWhere || {}),
          ...currentFilters,
        });
      });
    }
  }

  onChangeFilter = (type, value, field) => {
    const where = { ...(this.view.state.filters || {}) };
    switch (type) {
      case "Pointer":
        if (value && Object.keys(value).length > 0) {
          where[field] = { id: value.id };
        } else {
          delete where[field];
        }
        break;
      case "Boolean":
        if (value !== null) where[field] = value;
        else delete where[field];
        break;
      default:
        if (value) where[field] = { $regex: value, $options: "i" };
        else delete where[field];
    }
    if (field === "sex") {
      if (value) where[field] = value.toUpperCase();
      else delete where[field];
    }
    this.view.setState({ filters: where });
    this.setReportFilter("collectionWhere", {
      ...(this.getReportFilters().collectionWhere || {}),
      ...where,
    });
  };

  getExportSchema(report) {
    if (report.mode === "service") {
      return buildSchemaFromColumns(report.columns || [], report.key);
    }
    return this.view.getSchema(report.collection);
  }

  clearExportBusy() {
    this.view.setState({ isLoading: false });
    this.closeBusyDialog();
  }

  async exportToCSV() {
    const report = this.getSelectedReport();
    if (report.export?.enabled === false) return;

    const filters = this.getReportFilters();
    if (this.allObjects.length === 0 && this.reportRequiresEmployeeScope(report)) {
      try {
        this.validateFilters(report, filters);
        this.reportService.assertEmployeeScope(filters, { required: true });
      } catch (e) {
        const message = e?.message || String(e);
        this.view.setState({ error: null, isLoading: false });
        this.view.showError(message);
        return;
      }
    }

    const exportName = report.name;
    this.view.setState({ isLoading: true });
    this.view.showBusyDialog?.("Exporting report...");

    // Close busy BEFORE showSuccess/showError. nq-component dialog stacks
    // nodes; closing after success only pops the success layer and leaves
    // the spinner open forever.
    try {
      if (report.mode === "service") {
        const rows =
          this.allObjects.length > 0
            ? this.allObjects
            : await this.reportService.run(report.service, filters);
        const schema = this.getExportSchema(report);
        const fields = Object.keys(schema.fields);
        await ExportCSVFunction(rows, exportName, schema, fields);
      } else {
        const schema = this.getExportSchema(report);
        if (schema?.fields?.position) {
          schema.fields.position.label = "Position";
        }
        if (exportName === "Employee records") {
          await this.exportEmployeeRecords(exportName, schema);
          this.clearExportBusy();
          this.view.showSuccess("Export completed");
          return;
        }
        let fields = getReportDisplayFields(report);
        if (report.name === "Organizational Structure") {
          fields = [...fields, ...ORG_HIERARCHY_FIELDS];
        }
        const objects = await this.findObjectUseCase.execute(
          this.view.getCollectionName(),
          {
            where: {
              ...this.createQuery().where,
              "acl.read": { $exists: true },
            },
            include: ["employee", "position", "employmentType"],
            keys: [...fields, "position.position_title", "employmentType.name"],
          },
        );

        const alignedObjects = await this.alignExportObjectsWithTable(
          objects,
          report,
        );

        const employmentTypeOrder = {
          PERMANENT: 1,
          Coterminous: 2,
          Contractual: 3,
          "Job Order": 4,
        };
        const sortedObjects = alignedObjects
          .map((obj, index) => ({ ...obj, originalIndex: index }))
          .sort((a, b) => {
            const orderA = employmentTypeOrder[a.employmentType?.name] || 999;
            const orderB = employmentTypeOrder[b.employmentType?.name] || 999;
            if (orderA !== orderB) return orderA - orderB;
            return a.originalIndex - b.originalIndex;
          })
          .map(({ originalIndex, ...obj }) => obj);

        if (hasOrganizationFields(fields)) {
          const maps = await loadOrganizationNameMaps(this.findObjectUseCase);
          resolveOrganizationFields(sortedObjects, maps);
        }
        await ExportCSVFunction(sortedObjects, exportName, schema, fields);
      }
      this.clearExportBusy();
      this.view.showSuccess("Export completed");
    } catch (e) {
      console.error(e.message || e);
      this.clearExportBusy();
      this.view.showError(this.formatReportError(e));
    }
  }

  async exportEmployeeRecords(exportName, schema) {
    const exclude = [
      "id",
      "acl",
      "creator",
      "editor",
      "createdAt",
      "updatedAt",
      "picture",
      "code",
      "selectedSchedule",
      "nonRegPosition",
      "attachments",
      "personalDataSheet",
      "appointment",
      "positionDescriptionFrom",
      "oathOfOffice",
      "serviceRecord",
      "AssumptionOfDuty",
      "contractOfService",
      "copiesOfCertificatedEligibilities",
      "copiesOfDiploma",
      "tor",
      "conventions",
      "copyOfBirth",
      "medicalCertificated",
      "nbiClearance",
      "NoticeOfSalaryAdjustments",
      "personalOrders",
      "letterDirective",
      "travelOrders",
      "copiesOfDisciplinaryActions",
      "individualPerformance",
      "swornStatementOfAssets",
      "othersFile",
      "positionTitle",
      "positionLabel",
      "effectivityDate",
      "isTriggered",
      "userID",
      "salaryPointer",
      "schedule",
      "allowance",
      "payrollRelation",
      "lastSync",
      "month",
      "year",
      "dailyRate",
      "payrollType",
      "payrollTypes",
      "payItems",
      "earnings",
      "payrollClassification",
      "rate",
      "taxWithHeldAmount",
      "payrollYear",
      "payrollMonth",
      "isMonthCredAdded",
      "isEmpNewlyHired",
      "payCodeDate",
      "basicTax",
      "apTax",
      "individualTax",
      "monthlyTax",
      "annualTax",
      "netOfMandatory",
      "netEarned",
      "monthlyPayCode",
      "mandatoryDeductions",
      "monthForContributions",
      "earnedBasic",
      "daysToBeDeducted",
      "halfDaysToBeDeducted",
      "minutesToBeDeducted",
      "daysInAmonth",
      "gsisInsuredDays",
      "committeeRelation",
      "percentTag",
      "earnedForThePeriod",
      "payrollHeader",
      "scheduleHistory",
      "selected_schedule_assignment",
      "payCode",
      "suspension",
      "dateOfSuspension",
      "oldRate",
      "oldSalaryGrade",
      "oldPositionTitle",
      "classification",
    ];
    let empRecFields = Object.keys(schema.fields).filter(
      (item) => !exclude.includes(item),
    );
    // Always include designations even if schema marks it non-readable.
    if (!empRecFields.includes("designations")) {
      empRecFields = [...empRecFields, "designations"];
    }
    const exportSchema = {
      ...schema,
      fields: {
        ...(schema.fields || {}),
        designations: {
          ...(schema.fields?.designations || {}),
          type: "String",
          label: schema.fields?.designations?.label || "Designation",
          read: true,
        },
      },
    };
    const objects = await this.findObjectUseCase.execute(
      this.view.getCollectionName(),
      {
        where: {
          ...this.createQuery().where,
          "acl.read": { $exists: true },
        },
        include: [
          "employee",
          "position",
          "employmentType",
          "supervisor",
          "nonRegPosition",
          "designations",
        ],
        keys: [
          ...empRecFields,
          "position.position_title",
          "employmentType.name",
          "supervisor",
          "nonRegPosition",
          "nonRegPosition.positionTitle",
          "nonRegPosition.montlhyRate",
          "designations",
        ],
      },
    );

    const alignedObjects = await this.alignExportObjectsWithTable(
      objects,
      this.getSelectedReport(),
    );

    // designations often come back as pointer ids only — resolve names from nature_functions.
    const designationNameById = new Map();
    try {
      const natures =
        (await this.findObjectUseCase.execute("nature_functions", {
          keys: ["name"],
          limit: 5000,
        })) || [];
      natures.forEach((n) => {
        if (n?.id) designationNameById.set(n.id, n.name || "");
      });
    } catch (_) {
      /* keep empty map — fall back to embedded name if present */
    }
    const formatDesignations = (value) => {
      const list = Array.isArray(value) ? value : value ? [value] : [];
      return list
        .map((d) => {
          if (!d) return "";
          if (typeof d === "string") return designationNameById.get(d) || "";
          return d.name || designationNameById.get(d.id) || "";
        })
        .filter(Boolean)
        .join(", ");
    };
    alignedObjects.forEach((obj) => {
      obj.designations = formatDesignations(obj.designations);
    });

    if (hasOrganizationFields(empRecFields)) {
      const maps = await loadOrganizationNameMaps(this.findObjectUseCase);
      resolveOrganizationFields(alignedObjects, maps);
    }
    await ExportCSVFunction(alignedObjects, exportName, exportSchema, empRecFields);
  }

  getMonths() {
    return MONTHS;
  }
}

export default ReportPagePresenter;
